import { RiskLevel } from '../types'

export interface ChatOption {
  id: string
  label: { en: string; id: string }
  riskScore: number
  feedback: { en: string; id: string }
}

export interface ChatQuestion {
  id: string
  message: { en: string; id: string }
  options: ChatOption[]
}

export interface ChatResultContent {
  title: { en: string; id: string }
  description: { en: string; id: string }
  actions: { en: string; id: string }[]
}

export interface ChatScenario {
  id: string
  title: { en: string; id: string }
  intro: { en: string; id: string }
  questions: ChatQuestion[]
  results: {
    high: ChatResultContent
    medium: ChatResultContent
    low: ChatResultContent
  }
}

export const chatScenarios: ChatScenario[] = [
  {
    id: 'social-engineering',
    title: {
      en: 'Social Engineering Check',
      id: 'Cek Rekayasa Sosial',
    },
    intro: {
      en: "Hello! I'm your genZAFE Assistant. I'll ask a few questions to help you determine if you're being targeted by a social engineering scam. Let's start.",
      id: 'Halo! Saya Asisten genZAFE Anda. Saya akan mengajukan beberapa pertanyaan untuk membantu menentukan apakah Anda menjadi target penipuan rekayasa sosial. Mari kita mulai.',
    },
    questions: [
      {
        id: 'contact_method',
        message: {
          en: 'How did this person or organization first contact you?',
          id: 'Bagaimana orang atau organisasi ini pertama kali menghubungi Anda?',
        },
        options: [
          {
            id: 'unsolicited_call',
            label: { en: 'Unexpected Phone Call', id: 'Telepon Tiba-tiba' },
            riskScore: 2,
            feedback: {
              en: 'Scammers often use cold calls to catch you off guard.',
              id: 'Penipu sering menggunakan panggilan tiba-tiba untuk membuat Anda lengah.',
            },
          },
          {
            id: 'whatsapp',
            label: { en: 'WhatsApp / Telegram', id: 'WhatsApp / Telegram' },
            riskScore: 2,
            feedback: {
              en: 'Official organizations rarely initiate contact via personal chat apps.',
              id: 'Organisasi resmi jarang memulai kontak melalui aplikasi chat pribadi.',
            },
          },
          {
            id: 'official_channel',
            label: { en: 'Official App/Website', id: 'Aplikasi/Website Resmi' },
            riskScore: 0,
            feedback: {
              en: 'Contact through official channels is usually safer, but always verify.',
              id: 'Kontak melalui saluran resmi biasanya lebih aman, tetapi selalu verifikasi.',
            },
          },
        ],
      },
      {
        id: 'urgency',
        message: {
          en: 'Are they pressuring you to act immediately (e.g., "your account will be blocked")?',
          id: 'Apakah mereka mendesak Anda untuk bertindak segera (misalnya, "akun Anda akan diblokir")?',
        },
        options: [
          {
            id: 'yes_urgent',
            label: { en: 'Yes, very urgent', id: 'Ya, sangat mendesak' },
            riskScore: 3,
            feedback: {
              en: 'Creating a false sense of urgency is a classic manipulation tactic.',
              id: 'Menciptakan rasa urgensi palsu adalah taktik manipulasi klasik.',
            },
          },
          {
            id: 'no_pressure',
            label: { en: 'No, they are patient', id: 'Tidak, mereka sabar' },
            riskScore: 0,
            feedback: {
              en: 'Legitimate representatives usually give you time to think and verify.',
              id: 'Perwakilan resmi biasanya memberi Anda waktu untuk berpikir dan memverifikasi.',
            },
          },
        ],
      },
      {
        id: 'request',
        message: {
          en: 'What are they asking you to do?',
          id: 'Apa yang mereka minta Anda lakukan?',
        },
        options: [
          {
            id: 'transfer',
            label: { en: 'Transfer Money', id: 'Transfer Uang' },
            riskScore: 3,
            feedback: {
              en: 'Requests to transfer money to "safe accounts" are almost always scams.',
              id: 'Permintaan transfer uang ke "rekening aman" hampir selalu penipuan.',
            },
          },
          {
            id: 'credentials',
            label: { en: 'Share Password/OTP', id: 'Bagikan Password/OTP' },
            riskScore: 3,
            feedback: {
              en: 'NEVER share your password or OTP with anyone, even bank staff.',
              id: 'JANGAN PERNAH membagikan password atau OTP kepada siapa pun, bahkan staf bank.',
            },
          },
          {
            id: 'update_info',
            label: { en: 'Update Personal Info', id: 'Update Info Pribadi' },
            riskScore: 1,
            feedback: {
              en: 'Be careful. Only update info directly on the official website, not via links.',
              id: 'Hati-hati. Hanya update info langsung di website resmi, bukan melalui link.',
            },
          },
        ],
      },
    ],
    results: {
      high: {
        title: { en: 'High Risk of Scam', id: 'Risiko Penipuan Tinggi' },
        description: {
          en: 'This shows strong signs of social engineering. Do not proceed.',
          id: 'Ini menunjukkan tanda-tanda kuat rekayasa sosial. Jangan lanjutkan.',
        },
        actions: [
          {
            en: 'Stop communication immediately',
            id: 'Hentikan komunikasi segera',
          },
          {
            en: 'Do not send money or info',
            id: 'Jangan kirim uang atau info',
          },
          {
            en: 'Call the official institution directly',
            id: 'Hubungi institusi resmi secara langsung',
          },
        ],
      },
      medium: {
        title: { en: 'Medium Risk', id: 'Risiko Menengah' },
        description: {
          en: 'There are some red flags. Proceed with extreme caution.',
          id: 'Ada beberapa tanda bahaya. Lanjutkan dengan sangat hati-hati.',
        },
        actions: [
          {
            en: 'Verify identity through another channel',
            id: 'Verifikasi identitas melalui saluran lain',
          },
          { en: 'Do not rush', id: 'Jangan terburu-buru' },
        ],
      },
      low: {
        title: { en: 'Low Risk', id: 'Risiko Rendah' },
        description: {
          en: 'This seems standard, but always stay vigilant.',
          id: 'Ini tampak standar, tetapi tetap waspada.',
        },
        actions: [
          { en: 'Monitor your accounts', id: 'Pantau akun Anda' },
          {
            en: 'Report if anything changes',
            id: 'Laporkan jika ada perubahan',
          },
        ],
      },
    },
  },
  {
    id: 'investment-crypto',
    title: {
      en: 'Investment Scam Check',
      id: 'Cek Penipuan Investasi',
    },
    intro: {
      en: "I can help you assess this investment opportunity. Let's look at the details.",
      id: 'Saya dapat membantu menilai peluang investasi ini. Mari kita lihat detailnya.',
    },
    questions: [
      {
        id: 'returns',
        message: {
          en: 'What kind of returns or profits are they promising?',
          id: 'Apa jenis keuntungan yang mereka janjikan?',
        },
        options: [
          {
            id: 'guaranteed_high',
            label: {
              en: 'Guaranteed High Returns',
              id: 'Keuntungan Tinggi Dijamin',
            },
            riskScore: 3,
            feedback: {
              en: 'There is no such thing as a "guaranteed" high return in investing. This is a major red flag.',
              id: 'Tidak ada yang namanya keuntungan tinggi "dijamin" dalam investasi. Ini tanda bahaya besar.',
            },
          },
          {
            id: 'market_rate',
            label: {
              en: 'Realistic/Market Rate',
              id: 'Realistis/Sesuai Pasar',
            },
            riskScore: 0,
            feedback: {
              en: 'Realistic returns are a good sign, but verify the platform.',
              id: 'Keuntungan realistis adalah tanda baik, tetapi verifikasi platformnya.',
            },
          },
        ],
      },
      {
        id: 'platform',
        message: {
          en: 'Where did you find this investment opportunity?',
          id: 'Di mana Anda menemukan peluang investasi ini?',
        },
        options: [
          {
            id: 'social_media',
            label: {
              en: 'Social Media / Group Chat',
              id: 'Media Sosial / Grup Chat',
            },
            riskScore: 2,
            feedback: {
              en: 'Many scams operate through Telegram groups or social media ads.',
              id: 'Banyak penipuan beroperasi melalui grup Telegram atau iklan media sosial.',
            },
          },
          {
            id: 'dating_app',
            label: {
              en: 'Dating App / New Friend',
              id: 'Aplikasi Kencan / Teman Baru',
            },
            riskScore: 3,
            feedback: {
              en: 'This is likely "Pig Butchering" scam. Strangers on dating apps rarely offer legitimate investments.',
              id: 'Ini kemungkinan penipuan "Pig Butchering". Orang asing di aplikasi kencan jarang menawarkan investasi sah.',
            },
          },
          {
            id: 'regulated_broker',
            label: { en: 'Regulated Broker', id: 'Broker Teregulasi' },
            riskScore: 0,
            feedback: {
              en: 'Always check if they are registered with your local financial authority.',
              id: 'Selalu periksa apakah mereka terdaftar di otoritas keuangan setempat.',
            },
          },
        ],
      },
    ],
    results: {
      high: {
        title: { en: 'High Risk Investment', id: 'Investasi Risiko Tinggi' },
        description: {
          en: 'This has characteristics of a Ponzi scheme or fake platform.',
          id: 'Ini memiliki karakteristik skema Ponzi atau platform palsu.',
        },
        actions: [
          { en: 'Do not invest any money', id: 'Jangan investasikan uang' },
          {
            en: 'Exit immediately if you have deposited',
            id: 'Keluar segera jika sudah deposit',
          },
        ],
      },
      medium: {
        title: { en: 'Medium Risk', id: 'Risiko Menengah' },
        description: {
          en: 'Unregulated platforms carry high risk even if not outright scams.',
          id: 'Platform tidak teregulasi membawa risiko tinggi meskipun bukan penipuan langsung.',
        },
        actions: [
          { en: 'Check official registration', id: 'Cek pendaftaran resmi' },
          { en: 'Read independent reviews', id: 'Baca ulasan independen' },
        ],
      },
      low: {
        title: { en: 'Lower Risk', id: 'Risiko Lebih Rendah' },
        description: {
          en: 'Appears to be a standard investment, but capital is always at risk.',
          id: 'Tampaknya investasi standar, tetapi modal selalu berisiko.',
        },
        actions: [
          {
            en: 'Invest only what you can lose',
            id: 'Investasi hanya yang Anda mampu rugi',
          },
        ],
      },
    },
  },
  {
    id: 'romance-scam',
    title: {
      en: 'Romance Scam Check',
      id: 'Cek Penipuan Asmara',
    },
    intro: {
      en: "Online relationships can be tricky. I'll help you spot if this person is genuine.",
      id: 'Hubungan online bisa rumit. Saya akan membantu Anda melihat apakah orang ini asli.',
    },
    questions: [
      {
        id: 'meeting',
        message: {
          en: 'Have you met this person in real life or on a video call?',
          id: 'Apakah Anda pernah bertemu orang ini secara langsung atau video call?',
        },
        options: [
          {
            id: 'never',
            label: { en: 'No, never', id: 'Tidak, tidak pernah' },
            riskScore: 2,
            feedback: {
              en: 'Scammers often have excuses (camera broken, bad internet) to avoid being seen.',
              id: 'Penipu sering punya alasan (kamera rusak, internet buruk) untuk menghindari terlihat.',
            },
          },
          {
            id: 'yes_video',
            label: { en: 'Yes, video called', id: 'Ya, pernah video call' },
            riskScore: 0,
            feedback: {
              en: 'Video calls are better, but deepfakes exist. Watch for glitches.',
              id: 'Video call lebih baik, tapi deepfake ada. Perhatikan gangguan gambar.',
            },
          },
        ],
      },
      {
        id: 'money',
        message: {
          en: 'Has this person asked you for money or gifts?',
          id: 'Apakah orang ini meminta uang atau hadiah?',
        },
        options: [
          {
            id: 'yes_emergency',
            label: { en: 'Yes, for an emergency', id: 'Ya, untuk darurat' },
            riskScore: 3,
            feedback: {
              en: 'Emergencies (medical, travel, customs) are the most common scam stories.',
              id: 'Keadaan darurat (medis, perjalanan, bea cukai) adalah cerita penipuan paling umum.',
            },
          },
          {
            id: 'yes_investment',
            label: { en: 'Yes, for investment', id: 'Ya, untuk investasi' },
            riskScore: 3,
            feedback: {
              en: 'Romance scammers often try to "teach" you how to invest (Pig Butchering).',
              id: 'Penipu asmara sering mencoba "mengajar" Anda cara berinvestasi (Pig Butchering).',
            },
          },
          {
            id: 'no',
            label: { en: 'No', id: 'Tidak' },
            riskScore: 0,
            feedback: {
              en: 'Good. Be very suspicious if they ever do ask.',
              id: 'Bagus. Sangat curiga jika mereka nanti meminta.',
            },
          },
        ],
      },
    ],
    results: {
      high: {
        title: {
          en: 'High Risk of Romance Scam',
          id: 'Risiko Penipuan Asmara Tinggi',
        },
        description: {
          en: 'This person is likely using a fake identity to exploit you.',
          id: 'Orang ini kemungkinan menggunakan identitas palsu untuk mengeksploitasi Anda.',
        },
        actions: [
          { en: 'Stop sending money', id: 'Berhenti kirim uang' },
          {
            en: 'Do a reverse image search of their photos',
            id: 'Lakukan pencarian gambar balik foto mereka',
          },
        ],
      },
      medium: {
        title: { en: 'Medium Risk', id: 'Risiko Menengah' },
        description: {
          en: 'There are suspicious signs. Proceed with caution.',
          id: 'Ada tanda-tanda mencurigakan. Lanjutkan dengan hati-hati.',
        },
        actions: [
          { en: 'Insist on a video call', id: 'Bersikeras untuk video call' },
          {
            en: 'Ask specific questions about their background',
            id: 'Tanya detail tentang latar belakang mereka',
          },
        ],
      },
      low: {
        title: { en: 'Low Risk', id: 'Risiko Rendah' },
        description: {
          en: 'No major red flags yet, but stay safe online.',
          id: 'Belum ada tanda bahaya besar, tapi tetap aman online.',
        },
        actions: [
          {
            en: 'Never share financial details',
            id: 'Jangan bagikan detail keuangan',
          },
        ],
      },
    },
  },
  {
    id: 'phishing-account-takeover',
    title: {
      en: 'Phishing Check',
      id: 'Cek Phishing',
    },
    intro: {
      en: "Received a suspicious message or email? I'll help you verify if it's a phishing attempt.",
      id: 'Menerima pesan atau email mencurigakan? Saya akan membantu memverifikasi apakah itu upaya phishing.',
    },
    questions: [
      {
        id: 'sender',
        message: {
          en: 'Check the sender address or phone number. Does it look official?',
          id: 'Periksa alamat pengirim atau nomor telepon. Apakah terlihat resmi?',
        },
        options: [
          {
            id: 'strange_domain',
            label: {
              en: 'Strange domain (e.g., support@bank-security-alert.com)',
              id: 'Domain aneh (misal: support@bank-security-alert.com)',
            },
            riskScore: 3,
            feedback: {
              en: 'Official organizations always use their main domain (e.g., @bank.com). Subdomains or variations are red flags.',
              id: 'Organisasi resmi selalu menggunakan domain utama mereka (misal: @bank.com). Subdomain atau variasi adalah tanda bahaya.',
            },
          },
          {
            id: 'public_email',
            label: {
              en: 'Public email (Gmail, Yahoo, Hotmail)',
              id: 'Email publik (Gmail, Yahoo, Hotmail)',
            },
            riskScore: 3,
            feedback: {
              en: 'Banks and official companies NEVER use public email domains for official business.',
              id: 'Bank dan perusahaan resmi TIDAK PERNAH menggunakan domain email publik untuk urusan resmi.',
            },
          },
          {
            id: 'official_domain',
            label: {
              en: 'Looks official (matches company website)',
              id: 'Terlihat resmi (cocok dengan website perusahaan)',
            },
            riskScore: 0,
            feedback: {
              en: "Good, but scammers can sometimes spoof addresses. Let's check the content.",
              id: 'Bagus, tapi penipu terkadang bisa memalsukan alamat. Mari periksa isinya.',
            },
          },
        ],
      },
      {
        id: 'link',
        message: {
          en: 'Does the message contain a link? Where does it lead?',
          id: 'Apakah pesan berisi tautan? Ke mana arahnya?',
        },
        options: [
          {
            id: 'shortened',
            label: {
              en: 'Shortened link (bit.ly, tinyurl)',
              id: 'Tautan pendek (bit.ly, tinyurl)',
            },
            riskScore: 2,
            feedback: {
              en: 'Shortened links hide the true destination. Be very careful.',
              id: 'Tautan pendek menyembunyikan tujuan sebenarnya. Sangat berhati-hati.',
            },
          },
          {
            id: 'login_page',
            label: {
              en: 'Link to a login page asking for password',
              id: 'Tautan ke halaman login meminta password',
            },
            riskScore: 3,
            feedback: {
              en: 'NEVER click links to login. Always type the official website URL yourself.',
              id: 'JANGAN PERNAH klik tautan untuk login. Selalu ketik URL website resmi sendiri.',
            },
          },
          {
            id: 'no_link',
            label: {
              en: 'No link, just information',
              id: 'Tidak ada tautan, hanya informasi',
            },
            riskScore: 0,
            feedback: {
              en: 'Informational messages are usually safer, but still verify the source.',
              id: 'Pesan informasi biasanya lebih aman, tetapi tetap verifikasi sumbernya.',
            },
          },
        ],
      },
      {
        id: 'urgency_phishing',
        message: {
          en: 'Is the message threatening you?',
          id: 'Apakah pesan tersebut mengancam Anda?',
        },
        options: [
          {
            id: 'account_closure',
            label: {
              en: 'Yes, "Account will be closed/suspended"',
              id: 'Ya, "Akun akan ditutup/dibekukan"',
            },
            riskScore: 3,
            feedback: {
              en: 'Fear tactics are the #1 sign of phishing. They want you to panic and click.',
              id: 'Taktik menakut-nakuti adalah tanda #1 phishing. Mereka ingin Anda panik dan klik.',
            },
          },
          {
            id: 'verify_identity',
            label: {
              en: 'Yes, "Verify identity immediately"',
              id: 'Ya, "Verifikasi identitas segera"',
            },
            riskScore: 3,
            feedback: {
              en: 'Legitimate verification requests usually happen when YOU initiate an action, not randomly.',
              id: 'Permintaan verifikasi yang sah biasanya terjadi saat ANDA memulai tindakan, bukan secara acak.',
            },
          },
          {
            id: 'no_threat',
            label: {
              en: 'No threats',
              id: 'Tidak ada ancaman',
            },
            riskScore: 0,
            feedback: {
              en: 'Lack of urgency is a good sign.',
              id: 'Kurangnya urgensi adalah tanda baik.',
            },
          },
        ],
      },
    ],
    results: {
      high: {
        title: { en: 'High Risk of Phishing', id: 'Risiko Phishing Tinggi' },
        description: {
          en: 'This message has clear signs of a phishing attack designed to steal your credentials.',
          id: 'Pesan ini memiliki tanda-tanda jelas serangan phishing yang dirancang untuk mencuri kredensial Anda.',
        },
        actions: [
          {
            en: 'Do NOT click any links',
            id: 'JANGAN klik tautan apa pun',
          },
          {
            en: 'Mark as spam/junk',
            id: 'Tandai sebagai spam/sampah',
          },
          {
            en: 'Change passwords if you clicked anything',
            id: 'Ganti password jika Anda mengklik sesuatu',
          },
        ],
      },
      medium: {
        title: { en: 'Suspicious Message', id: 'Pesan Mencurigakan' },
        description: {
          en: 'It might be phishing. Verify directly with the company using their official app or website.',
          id: 'Ini mungkin phishing. Verifikasi langsung dengan perusahaan menggunakan aplikasi atau website resmi mereka.',
        },
        actions: [
          {
            en: 'Contact support via official website',
            id: 'Hubungi dukungan melalui website resmi',
          },
          {
            en: 'Do not reply to the message',
            id: 'Jangan balas pesan tersebut',
          },
        ],
      },
      low: {
        title: { en: 'Likely Safe', id: 'Kemungkinan Aman' },
        description: {
          en: 'Appears legitimate, but always be cautious with personal information.',
          id: 'Tampaknya sah, tetapi selalu berhati-hati dengan informasi pribadi.',
        },
        actions: [
          {
            en: 'Proceed with caution',
            id: 'Lanjutkan dengan hati-hati',
          },
        ],
      },
    },
  },
  {
    id: 'marketplace-payment',
    title: {
      en: 'Marketplace Scam Check',
      id: 'Cek Penipuan Marketplace',
    },
    intro: {
      en: "Buying or selling online? Let's check if this deal is safe before you proceed.",
      id: 'Membeli atau menjual online? Mari periksa apakah kesepakatan ini aman sebelum Anda melanjutkan.',
    },
    questions: [
      {
        id: 'platform_payment',
        message: {
          en: 'How does the other party want to handle payment?',
          id: 'Bagaimana pihak lain ingin menangani pembayaran?',
        },
        options: [
          {
            id: 'outside_app',
            label: {
              en: 'Direct transfer / Outside the app',
              id: 'Transfer langsung / Di luar aplikasi',
            },
            riskScore: 3,
            feedback: {
              en: 'Moving off-platform removes all buyer/seller protection. This is the most common marketplace scam.',
              id: 'Keluar dari platform menghilangkan semua perlindungan pembeli/penjual. Ini adalah penipuan marketplace paling umum.',
            },
          },
          {
            id: 'in_app',
            label: {
              en: "Through the platform's secure system",
              id: 'Melalui sistem aman platform',
            },
            riskScore: 0,
            feedback: {
              en: 'Always stick to the official payment system for protection.',
              id: 'Selalu gunakan sistem pembayaran resmi untuk perlindungan.',
            },
          },
        ],
      },
      {
        id: 'price_check',
        message: {
          en: 'How is the price compared to other similar items?',
          id: 'Bagaimana harganya dibandingkan dengan barang serupa lainnya?',
        },
        options: [
          {
            id: 'too_cheap',
            label: {
              en: 'Much cheaper (Too good to be true)',
              id: 'Jauh lebih murah (Terlalu bagus untuk jadi kenyataan)',
            },
            riskScore: 2,
            feedback: {
              en: 'Scammers use low prices to lure victims quickly.',
              id: 'Penipu menggunakan harga rendah untuk memikat korban dengan cepat.',
            },
          },
          {
            id: 'market_price',
            label: {
              en: 'Normal market price',
              id: 'Harga pasar normal',
            },
            riskScore: 0,
            feedback: {
              en: 'Reasonable pricing is a good sign.',
              id: 'Harga yang wajar adalah tanda baik.',
            },
          },
        ],
      },
      {
        id: 'seller_profile',
        message: {
          en: 'Check the seller/buyer profile. What do you see?',
          id: 'Periksa profil penjual/pembeli. Apa yang Anda lihat?',
        },
        options: [
          {
            id: 'new_account',
            label: {
              en: 'New account, no reviews',
              id: 'Akun baru, tidak ada ulasan',
            },
            riskScore: 2,
            feedback: {
              en: 'Be cautious with new accounts, especially for high-value items.',
              id: 'Berhati-hatilah dengan akun baru, terutama untuk barang bernilai tinggi.',
            },
          },
          {
            id: 'established',
            label: {
              en: 'Old account with good reviews',
              id: 'Akun lama dengan ulasan bagus',
            },
            riskScore: 0,
            feedback: {
              en: 'Established accounts are generally safer, but hacked accounts exist.',
              id: 'Akun lama umumnya lebih aman, tetapi akun yang diretas ada.',
            },
          },
        ],
      },
    ],
    results: {
      high: {
        title: { en: 'High Risk Transaction', id: 'Transaksi Risiko Tinggi' },
        description: {
          en: 'Do not proceed. The request to pay outside the platform or unusual pricing indicates a scam.',
          id: 'Jangan lanjutkan. Permintaan untuk membayar di luar platform atau harga yang tidak wajar menunjukkan penipuan.',
        },
        actions: [
          {
            en: 'Cancel the deal',
            id: 'Batalkan kesepakatan',
          },
          {
            en: 'Report the user to the platform',
            id: 'Laporkan pengguna ke platform',
          },
        ],
      },
      medium: {
        title: { en: 'Medium Risk', id: 'Risiko Menengah' },
        description: {
          en: "There are some concerns. Only proceed if you can use the platform's secure payment system.",
          id: 'Ada beberapa kekhawatiran. Hanya lanjutkan jika Anda dapat menggunakan sistem pembayaran aman platform.',
        },
        actions: [
          {
            en: 'Insist on in-app payment',
            id: 'Bersikeras pada pembayaran dalam aplikasi',
          },
          {
            en: 'Ask for more photos/details',
            id: 'Minta lebih banyak foto/detail',
          },
        ],
      },
      low: {
        title: { en: 'Likely Safe', id: 'Kemungkinan Aman' },
        description: {
          en: 'Standard transaction. Keep communication and payment inside the app.',
          id: 'Transaksi standar. Tetap jaga komunikasi dan pembayaran di dalam aplikasi.',
        },
        actions: [
          {
            en: 'Follow platform guidelines',
            id: 'Ikuti pedoman platform',
          },
        ],
      },
    },
  },
  {
    id: 'sextortion-blackmail',
    title: {
      en: 'Sextortion Check',
      id: 'Cek Sextortion',
    },
    intro: {
      en: "This is a sensitive situation. I'm here to help you assess the threat level and what to do next. Your safety is priority.",
      id: 'Ini situasi sensitif. Saya di sini untuk membantu menilai tingkat ancaman dan apa yang harus dilakukan selanjutnya. Keselamatan Anda adalah prioritas.',
    },
    questions: [
      {
        id: 'threat_content',
        message: {
          en: 'What are they threatening to share?',
          id: 'Apa yang mereka ancam akan bagikan?',
        },
        options: [
          {
            id: 'nude_photos',
            label: {
              en: 'Intimate photos/videos I sent them',
              id: 'Foto/video intim yang saya kirimkan',
            },
            riskScore: 3,
            feedback: {
              en: 'This is classic sextortion. They use your fear to control you.',
              id: 'Ini sextortion klasik. Mereka menggunakan ketakutan Anda untuk mengendalikan Anda.',
            },
          },
          {
            id: 'recorded_video',
            label: {
              en: 'Video recorded during a call',
              id: 'Video yang direkam selama panggilan',
            },
            riskScore: 3,
            feedback: {
              en: 'Scammers often record video calls secretly to blackmail victims.',
              id: 'Penipu sering merekam panggilan video secara diam-diam untuk memeras korban.',
            },
          },
          {
            id: 'fake_claim',
            label: {
              en: 'They claim to have access (no proof)',
              id: 'Mereka mengaku punya akses (tanpa bukti)',
            },
            riskScore: 1,
            feedback: {
              en: "If they haven't shown proof, they might be bluffing.",
              id: 'Jika mereka belum menunjukkan bukti, mereka mungkin menggertak.',
            },
          },
        ],
      },
      {
        id: 'demand',
        message: {
          en: 'What are they demanding from you?',
          id: 'Apa yang mereka tuntut dari Anda?',
        },
        options: [
          {
            id: 'money_demand',
            label: {
              en: 'Money / Crypto / Gift Cards',
              id: 'Uang / Kripto / Kartu Hadiah',
            },
            riskScore: 3,
            feedback: {
              en: 'Financial motivation confirms it is a scam. Paying rarely stops them.',
              id: 'Motivasi finansial menegaskan itu penipuan. Membayar jarang menghentikan mereka.',
            },
          },
          {
            id: 'more_content',
            label: {
              en: 'More photos/videos',
              id: 'Lebih banyak foto/video',
            },
            riskScore: 3,
            feedback: {
              en: 'Do not comply. They will only use new content to threaten you further.',
              id: 'Jangan patuhi. Mereka hanya akan menggunakan konten baru untuk mengancam Anda lebih jauh.',
            },
          },
        ],
      },
      {
        id: 'payment_status',
        message: {
          en: 'Have you paid them anything yet?',
          id: 'Apakah Anda sudah membayar mereka?',
        },
        options: [
          {
            id: 'paid',
            label: {
              en: 'Yes, I paid',
              id: 'Ya, saya sudah bayar',
            },
            riskScore: 3,
            feedback: {
              en: 'Paying marks you as a target who pays. They will likely ask for more.',
              id: 'Membayar menandai Anda sebagai target yang membayar. Mereka kemungkinan akan meminta lagi.',
            },
          },
          {
            id: 'not_paid',
            label: {
              en: 'No, not yet',
              id: 'Tidak, belum',
            },
            riskScore: 0,
            feedback: {
              en: 'Good. DO NOT PAY. Paying does not guarantee they will delete anything.',
              id: 'Bagus. JANGAN BAYAR. Membayar tidak menjamin mereka akan menghapus apa pun.',
            },
          },
        ],
      },
    ],
    results: {
      high: {
        title: {
          en: 'High Danger - Sextortion',
          id: 'Bahaya Tinggi - Sextortion',
        },
        description: {
          en: 'You are a victim of sextortion. Do not pay. Paying funds organized crime and does not end the threats.',
          id: 'Anda adalah korban sextortion. Jangan bayar. Membayar mendanai kejahatan terorganisir dan tidak mengakhiri ancaman.',
        },
        actions: [
          {
            en: 'STOP all communication immediately',
            id: 'HENTIKAN semua komunikasi segera',
          },
          {
            en: 'Do NOT pay anything',
            id: 'JANGAN bayar apa pun',
          },
          {
            en: 'Screenshot everything for evidence',
            id: 'Screenshot semuanya untuk bukti',
          },
          {
            en: 'Block them on all platforms',
            id: 'Blokir mereka di semua platform',
          },
        ],
      },
      medium: {
        title: { en: 'Potential Threat', id: 'Ancaman Potensial' },
        description: {
          en: 'Even if they are bluffing, take it seriously. Protect your privacy.',
          id: 'Bahkan jika mereka menggertak, anggap serius. Lindungi privasi Anda.',
        },
        actions: [
          {
            en: 'Change your privacy settings',
            id: 'Ubah pengaturan privasi Anda',
          },
          {
            en: 'Do not engage further',
            id: 'Jangan terlibat lebih jauh',
          },
        ],
      },
      low: {
        title: { en: 'Low Risk', id: 'Risiko Rendah' },
        description: {
          en: 'Stay vigilant and protect your private information.',
          id: 'Tetap waspada dan lindungi informasi pribadi Anda.',
        },
        actions: [
          {
            en: 'Be careful sharing intimate content',
            id: 'Hati-hati membagikan konten intim',
          },
        ],
      },
    },
  },
  {
    id: 'job-scam',
    title: {
      en: 'Job Scam Check',
      id: 'Cek Penipuan Kerja',
    },
    intro: {
      en: "Job hunting? Let's verify if this job offer is legitimate or a trap.",
      id: 'Sedang cari kerja? Mari verifikasi apakah tawaran kerja ini sah atau jebakan.',
    },
    questions: [
      {
        id: 'payment_request',
        message: {
          en: 'Are they asking you to pay for anything (training, equipment, visa)?',
          id: 'Apakah mereka meminta Anda membayar sesuatu (pelatihan, peralatan, visa)?',
        },
        options: [
          {
            id: 'yes_fee',
            label: {
              en: 'Yes, upfront fees required',
              id: 'Ya, biaya di muka diperlukan',
            },
            riskScore: 3,
            feedback: {
              en: 'Legitimate employers pay YOU. They never ask for money upfront.',
              id: 'Pemberi kerja yang sah membayar ANDA. Mereka tidak pernah meminta uang di muka.',
            },
          },
          {
            id: 'no_fee',
            label: {
              en: 'No, no fees mentioned',
              id: 'Tidak, tidak ada biaya disebutkan',
            },
            riskScore: 0,
            feedback: {
              en: 'Good. Be careful if they ask later.',
              id: 'Bagus. Hati-hati jika mereka meminta nanti.',
            },
          },
        ],
      },
      {
        id: 'interview_process',
        message: {
          en: 'How was the interview conducted?',
          id: 'Bagaimana wawancara dilakukan?',
        },
        options: [
          {
            id: 'chat_only',
            label: {
              en: 'Text/Chat only (WhatsApp/Telegram)',
              id: 'Hanya Teks/Chat (WhatsApp/Telegram)',
            },
            riskScore: 3,
            feedback: {
              en: 'Real companies interview via phone, video, or in-person. Chat-only is a huge red flag.',
              id: 'Perusahaan asli mewawancarai via telepon, video, atau langsung. Chat-only adalah tanda bahaya besar.',
            },
          },
          {
            id: 'video_person',
            label: {
              en: 'Video call or In-person',
              id: 'Video call atau Langsung',
            },
            riskScore: 0,
            feedback: {
              en: 'Face-to-face interaction is a good sign of legitimacy.',
              id: 'Interaksi tatap muka adalah tanda baik legitimasi.',
            },
          },
        ],
      },
      {
        id: 'salary_offer',
        message: {
          en: 'Does the salary seem realistic for the work required?',
          id: 'Apakah gaji tampak realistis untuk pekerjaan yang diminta?',
        },
        options: [
          {
            id: 'too_high',
            label: {
              en: 'Very high for little work',
              id: 'Sangat tinggi untuk sedikit kerja',
            },
            riskScore: 2,
            feedback: {
              en: 'If it sounds too good to be true, it usually is.',
              id: 'Jika terdengar terlalu bagus untuk jadi kenyataan, biasanya memang begitu.',
            },
          },
          {
            id: 'market_rate',
            label: {
              en: 'Normal market rate',
              id: 'Tarif pasar normal',
            },
            riskScore: 0,
            feedback: {
              en: 'Realistic expectations are a positive sign.',
              id: 'Ekspektasi realistis adalah tanda positif.',
            },
          },
        ],
      },
    ],
    results: {
      high: {
        title: { en: 'High Risk Job Scam', id: 'Risiko Penipuan Kerja Tinggi' },
        description: {
          en: 'This offer has major red flags of a job scam. Do not pay or share personal info.',
          id: 'Tawaran ini memiliki tanda bahaya besar penipuan kerja. Jangan bayar atau bagikan info pribadi.',
        },
        actions: [
          {
            en: 'Stop communication',
            id: 'Hentikan komunikasi',
          },
          {
            en: 'Do NOT pay any fees',
            id: 'JANGAN bayar biaya apa pun',
          },
          {
            en: 'Do not share bank details/ID',
            id: 'Jangan bagikan detail bank/KTP',
          },
        ],
      },
      medium: {
        title: { en: 'Suspicious Offer', id: 'Tawaran Mencurigakan' },
        description: {
          en: 'Proceed with extreme caution. Verify the company independently.',
          id: 'Lanjutkan dengan sangat hati-hati. Verifikasi perusahaan secara independen.',
        },
        actions: [
          {
            en: 'Check company website/reviews',
            id: 'Cek website/ulasan perusahaan',
          },
          {
            en: 'Ask for a video interview',
            id: 'Minta wawancara video',
          },
        ],
      },
      low: {
        title: { en: 'Likely Legitimate', id: 'Kemungkinan Sah' },
        description: {
          en: 'Seems like a standard job process, but stay professional and alert.',
          id: 'Tampak seperti proses kerja standar, tetapi tetap profesional dan waspada.',
        },
        actions: [
          {
            en: 'Proceed with application',
            id: 'Lanjutkan lamaran',
          },
        ],
      },
    },
  },
]
